import { useState } from 'react';
import { numberWithCommas } from '../../api/utils/format/numbers';
import { openAlert } from '../../store/features/modalSlice';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import Inputs from '../common/Inputs';
import TextWinds from '../common/TextWinds';
import Buttons from '../common/Buttons2';
import { selectPoint } from '../../store/features/pointSlice';

interface MemberShopInfoProps {
  originPrice: number;
  setPointAmount: (value: number) => void;
}
/**
 *
 * @param originPrice 상품금액
 * @param setPointAmount 포인트 적용
 * @returns
 */
const MemberShipInfo = ({
  originPrice,
  setPointAmount,
}: MemberShopInfoProps) => {
  const dispatch = useAppDispatch();
  // 단위 금액
  const STANDARD_POINT = 1000;
  // 사용하려는 포인트
  const [redeemPoints, setRedeemPoints] = useState(0);
  const userPoints = useAppSelector(selectPoint);
  const points = userPoints?.balance || 0;

  /**
   * 포인트 사용 동작 조건:
   * 1. 사용 포인트는 보유 포인트를 초과할 수 없음
   * 2. 사용 포인트는 결제 예정액을 초과할 수 없음
   * 3. 0원은 항상 허용
   * 4. 전액 결제 조건: 보유 포인트 >= 결제 예정액 && 사용 포인트 >= 결제 예정액
   * 5. 전액 결제인 경우: 1000원 단위 제한 없음 (정확한 금액 사용 가능)
   * 6. 전액 결제가 아닌 경우: 1000원 단위로만 사용 가능 (1000원 미만이면 거부)
   */

  // 포인트 계산 (1000원 단위 버림 처리)
  const floor = (value: number) => {
    return Math.floor(value / STANDARD_POINT) * STANDARD_POINT;
  };

  // 알림창
  const Alert = (msg: string) => {
    dispatch(
      openAlert({
        message: msg,
      }),
    );
  };

  // 포인트 입력
  const handleInputChange = (value: string) => {
    const point: number = Number(value.replace(/[^0-9]/g, '') || 0);
    setRedeemPoints(point);
  };

  // 포인트 적용
  const handleApplyEvent = (inputPoints: number) => {
    // 1. 보유 포인트와 결제 예정액 중 작은 값으로 제한
    const maxAvailable = Math.min(points, originPrice);
    let finalPoint = Math.min(inputPoints, maxAvailable);

    // 2. 0원은 항상 허용
    if (finalPoint === 0) {
      setRedeemPoints(0);
      setPointAmount(0);
      Alert(`포인트가 해제 되었습니다.`);
      return;
    }

    // 3. 전액 결제 가능 여부 확인
    const isFullPayment = points >= originPrice && finalPoint >= originPrice;

    // 4. 전액 결제가 아닌 경우: 1000원 단위 체크
    if (!isFullPayment) {
      if (finalPoint < STANDARD_POINT) {
        Alert(
          `포인트는 ${numberWithCommas(
            STANDARD_POINT,
          )} 포인트 이상부터 사용할 수 있습니다.`,
        );
        setRedeemPoints(0);
        setPointAmount(0);
        return;
      }
      // 1000원 단위로 버림 처리
      finalPoint = floor(finalPoint);
    }

    // 5. 최종 적용
    setRedeemPoints(finalPoint);
    setPointAmount(finalPoint);
    Alert(`${numberWithCommas(finalPoint)} 포인트가 적용 되었습니다.`);
  };

  return (
    // <div className="mt-[16px] bg-white px-[20px] py-[14px] xl:rounded-[8px]">
    //   <div className="mb-4 text-lg font-bold">포인트 적립 및 사용 안내</div>
    //   <strong>
    //     점검기간 : 10월 20일 월요일 오전 9시~ 11월 10일 월요일 오전 10시
    //   </strong>
    //   <br /> 긴급 기술 점검으로 인해, 포인트 시스템 관련 적립과 사용은
    //   한시적으로{' '}
    //   <strong>
    //     <span className="text-red5">전화로 문의</span>해주시는 고객님에 한해
    //     지원됩니다.
    //   </strong>{' '}
    //   보다 안정적인 서비스 제공을 위해 회원님의 양해 부탁드립니다.
    // </div>
    <div className="mt-[16px] bg-white px-[20px] py-[14px] xl:rounded-[8px]">
      {/* <div className="flex flex-row items-end"> */}
      <TextWinds type="title_h4"> 포인트</TextWinds>
      <TextWinds type="caption1" className="mt-1">
        * 포인트는 {numberWithCommas(STANDARD_POINT)}원 단위로 사용 가능하며,
        적용 버튼을 눌러주세요.
      </TextWinds>
      {/* </div> */}
      <TextWinds type="caption1" className="mt-1">
        * 포인트 전액 결제 시에도 결제수단을 선택해 주세요.
      </TextWinds>
      <div className="mt-[16px]">
        <Inputs
          // title="포인트"
          type="text-button"
          buttonText="적용"
          placeholder=""
          value={numberWithCommas(redeemPoints)}
          idDbCheck={redeemPoints >= 0}
          onChange={handleInputChange}
          onBlur={() => handleApplyEvent(redeemPoints)}
          onClick={() => handleApplyEvent(redeemPoints)}
          maxValue={points}
        />
        <div className="flex flex-row items-center justify-start gap-[8px]">
          <div className="flex gap-1 pt-2">
            <TextWinds type="content_rel">보유 포인트</TextWinds>
            <TextWinds type="content_body4">
              {points ? numberWithCommas(points) : 0}점
            </TextWinds>
          </div>

          <div className="pt-[8px]">
            <Buttons
              variant="filled"
              type="text"
              color="purple5"
              disabled={points > 0 ? false : true}
              onClick={() => handleApplyEvent(points)}
              className="rounded-md px-[6px] py-1"
            >
              <TextWinds type="content_rel" color="white">
                최대사용
              </TextWinds>
            </Buttons>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberShipInfo;
